import moment from 'moment-timezone';



/**
 * Format a time/date value as a locale-aware time-of-day string.
 *
 * Output is driven by `Intl.DateTimeFormat` against the raw
 * `navigator.language`, so every locale's CLDR hour cycle wins — en-US
 * "3:30 PM", en-GB "15:30", de-DE "15:30", ja-JP "15:30". Going through
 * Intl rather than moment's `LT` token avoids the allowlist fallback in
 * `constants/supported-locales.js` (which would force unlisted locales
 * back to 12-hour en-US output).
 *
 * @param {*} value - ISO string, Date, moment, or a parsable string when
 *   parseFormat is provided
 * @param {object} [options]
 * @param {string} [options.timeZone] - IANA zone (e.g. "America/Los_Angeles")
 * @param {string} [options.parseFormat] - moment parse format for non-ISO input
 * @returns {string}
 */
function formatTime(value, { timeZone, parseFormat } = {}) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const m = parseFormat ? moment(value, parseFormat) : moment(value);

  if (!m.isValid()) {
    return '';
  }

  const locale = window.navigator.language || 'en-US';
  const { hourCycle } = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions();
  const is24Hour = hourCycle === 'h23' || hourCycle === 'h24';

  // 12-hour locales render with no leading zero on the hour ("5:00 AM");
  // 24-hour locales render with the leading zero ("05:00"). Intl's
  // `hour: 'numeric'` drops the leading zero in both cycles, so branch
  // on the resolved hour cycle to get the CLDR-conventional rendering.
  const options = {
    hour: is24Hour ? '2-digit' : 'numeric',
    minute: '2-digit'
  };

  if (timeZone) {
    options.timeZone = timeZone;
  }

  return new Intl.DateTimeFormat(locale, options).format(m.toDate());
}



export default formatTime;
